/**
 * Sharetribe design tokens, mirrored verbatim from src/styles/marketplaceDefaults.css.
 * var(--x) indirections are resolved to their final value.
 *
 * Every value is the raw CSS string exactly as authored — including numeric-looking ones
 * (e.g. fontWeight '700', a '6px' spacing). They are consumed directly as CSS values in `sx`,
 * so they are intentionally NOT coerced to numbers (that would break unit-bearing values and
 * violate the verbatim mirror). This is distinct from create-globals.js, which holds
 * MUI-native numeric layout constants; create-tokens.js is the faithful CSS mirror.
 *
 * @returns {Record<string, string>}
 */
export const createTokens = () => ({
  marketplaceColor: '#4a6e91',
  marketplaceColorLight: '#eac435',
  marketplaceColorDark: '#0e3b43',
  marketplaceColorEncoded: '%23c0392b',
  successColor: '#2ecc71',
  successColorDark: '#239954',
  successColorLight: '#f0fff6',
  failColor: '#ff0000',
  failColorLight: '#ff4d4d',
  attentionColor: '#ffaa00',
  attentionColorLight: '#fff7f0',
  bannedColorLight: '#eac435',
  bannedColorDark: '#4a6e91',
  matterColorDark: '#000000',
  matterColor: '#4a4a4a',
  matterColorAnti: '#b2b2b2',
  matterColorNegative: '#e7e7e7',
  matterColorBright: '#fcfcfc',
  matterColorLight: '#ffffff',
  vansiteDarkGreen: '#0e3b43',
  matterColorLightTransparent: 'rgba(255, 255, 255, 0.65)',
  fontFamily: "'sofiapro', Helvetica, Arial, sans-serif",
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightSemiBold: '600',
  fontWeightBold: '700',
  fontWeightHighlightEmail: '700',
  spacingUnit: '6px',
  spacingUnitDesktop: '8px',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
  boxShadowTop: '0 -2px 4px 0 rgba(0, 0, 0, 0.1)',
  boxShadowLight: '0 2px 4px 0 rgba(0, 0, 0, 0.05)',
  boxShadowPopup: '0 8px 16px 0 rgba(0, 0, 0, 0.3)',
  boxShadowPopupLight: '0 3px 6px 0 rgba(0, 0, 0, 0.2)',
  boxShadowBreakdown: '0 3px 6px 0 rgba(0, 0, 0, 0.2)',
  boxShadowButton: '0 4px 8px 0 rgba(0, 0, 0, 0.1)',
  boxShadowListingCard: '0 0 50px 0 rgba(0, 0, 0, 0.1)',
  boxShadowNotFoundPageSearch: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
  boxShadowSectionLocationHover: '0 10px 30px 0 rgba(0, 0, 0, 0.1)',
  boxShadowBottomForm: '0 -2px 4px 0 rgba(0, 0, 0, 0.05)',
  boxShadowFilterButton: '0 4px 16px 0 rgba(0, 0, 0, 0.2)',
  zIndexTopbar: '10',
  zIndexPopup: '50',
  zIndexModal: '100',
  zIndexGenericError: '200',
  borderRadius: '2px',
  borderRadiusMobileSearch: '3px',
  transitionStyle: 'ease-in 0.2s',
  transitionStyleButton: 'ease-in-out 0.1s',
  topbarHeight: '60px',
  topbarHeightDesktop: '72px',
  TopbarMobileMenu_topMargin: '96px',
  Topbar_logoHeight: '25px',
  CheckoutPage_logoHeight: '25px',
  CheckoutPage_logoHeightDesktop: '27px',
  TopbarSearchForm_inputHeight: '53px',
  TopbarSearchForm_topbarMargin: '94px',
  TopbarSearchForm_bottomBorder: '3px',
  modalPadding: '24px 24px 48px 24px',
  modalPaddingMedium: '55px 60px 55px 60px',
  locationAutocompleteBottomPadding: '8px',
  LocationAutocompleteInput_inputHeight: '50px',
  LocationAutocompleteInput_sidePaddingDesktop: '36px',
  marketplaceButtonSmallDesktopPadding: '9px 16px 9px 16px',
  ReactDates_selectionHeight: '36px',
  ReactDates_hoveredOverlayColor: 'rgba(255, 255, 255, 0.2)',
  DateInput_selectionHeight: '36px',
  DateInput_hoveredOverlayColor: 'rgba(255, 255, 255, 0.2)',
  DateRangeInput_selectionHeight: '36px',
  DateRangeInput_hoveredOverlayColor: 'rgba(255, 255, 255, 0.2)',
  DateRangeController_selectionHeight: '36px',
  SectionHero_desktopTitleMaxWidth: '625px',
  TabNav_linkWidth: '240px',
  LandingPage_sectionMarginTop: '40px',
  LandingPage_sectionMarginTopMedium: '60px',
  LandingPage_sectionMarginTopLarge: '94px',
  ManageAvailabilityCalendar_gridColor: '#e0e0e0',
  ManageAvailabilityCalendar_availableColor: '#ffffff',
  ManageAvailabilityCalendar_availableColorHover: '#fafafa',
  ManageAvailabilityCalendar_blockedColor: '#ebebeb',
  ManageAvailabilityCalendar_blockedColorHover: '#e6e6e6',
  ManageAvailabilityCalendar_reservedColor: '#e6fff0',
  ManageAvailabilityCalendar_reservedColorHover: '#e1faeb',
  ManageAvailabilityCalendar_failedColor: '#fff2f2',
  ProfileSettingsForm_avatarSize: '96px',
  ProfileSettingsForm_avatarSizeDesktop: '240px',
  BottomBar_height: '80px',
  contentMaxWidth: '720px',
  listMaxWidth: '600px',
  textMaxWidth: '760px',
  formMaxWidth: '560px',
});
